import { useCallback, useEffect, useRef, useState } from 'react';

import { useConfluenceContent } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceContent';

import type { PublishedConfluenceData } from '../../../common/types';

const isRovoConfluenceSystemSpace = (spaceKey?: string | null) => {
	if (!spaceKey) {
		return false;
	}
	return spaceKey.startsWith('~ROVO') && spaceKey.endsWith('~');
};

type UseProductDataParams = {
	previewProduct: string;
	id: string;
	includeReactions: boolean; // Needed to ensure we get reactions for pages/docs but not for e.g. whiteboards
	status?: string; // Needed to fetch data when status changes to 'finished'
	cloudId: string;
};
export const useProductData = ({
	previewProduct,
	id,
	cloudId,
	includeReactions,
	status,
}: UseProductDataParams): {
	publishedContentData: PublishedConfluenceData | undefined;
} => {
	const [publishedContentData, setPublishedContentData] = useState<
		undefined | PublishedConfluenceData
	>(undefined);

	const { getConfluenceContent } = useConfluenceContent(id, cloudId, includeReactions);
	const previousStateRef = useRef<{ id: string; status?: string } | null>(null);

	const fetchData = useCallback(async () => {
		let fetchFunction;
		switch (previewProduct) {
			case 'confluence':
				fetchFunction = getConfluenceContent;
				break;
		}

		if (!fetchFunction) {
			return;
		}

		const fetchedData = await fetchFunction();

		if (fetchedData) {
			switch (previewProduct) {
				case 'confluence':
					if (!fetchedData?.content?.nodes?.[0]) {
						break;
					}
					// Status = finished should imply the content is not in the system space, but as the requests are to two different systemsnot a shared transaction, they can go out of sync
					// Therefore, we confirm here we're not in the system space. If we are able to improve how synchronous this logic is, we can remove this check.
					if (isRovoConfluenceSystemSpace(fetchedData?.content?.nodes?.[0]?.space?.key)) {
						break;
					}
					const result = {
						title: fetchedData?.content?.nodes?.[0]?.title,
						type: fetchedData?.content?.nodes?.[0]?.type,
						subType: fetchedData?.content?.nodes?.[0]?.subType,
						excerpt: fetchedData?.content?.nodes?.[0]?.excerpt,
						contributors: fetchedData?.contentContributors?.nodes.map((node) => ({
							name: node.displayName,
							profilePicture: node.profilePicture,
						})),
						reactions:
							fetchedData?.confluence_contentReactionsSummary?.contentReactionsSummary?.[0],
						links: fetchedData?.content?.nodes?.[0]?.links,
						lastUpdated: fetchedData?.content?.nodes?.[0]?.version?.friendlyWhen,
					};
					setPublishedContentData(result);
					break;
			}
		}
	}, [previewProduct, getConfluenceContent]);

	useEffect(() => {
		const shouldFetch =
			status === 'finished' &&
			(previousStateRef.current?.id !== id || previousStateRef.current?.status !== 'finished');

		if (shouldFetch) {
			fetchData();
		}

		previousStateRef.current = { id, status };
	}, [fetchData, id, status]);

	return {
		publishedContentData,
	};
};
